(function () {
  "use strict";

  let cartState = { items: [], item_count: 0 };
  let isInitialized = false;
  let cartCheckInterval = null;
  let pendingAdditions = new Set();
  let widgetLoadStates = new Map();

  // Initialize integration
  function init() {
    if (isInitialized) return;
    
    // Don't run on checkout or order status pages
    if (window.location.pathname.includes('/checkout') || 
        window.location.pathname.includes('/thank_you') ||
        window.location.pathname.includes('/orders/') ||
        window.Shopify?.designMode) {  // Don't intercept in theme editor
      console.log('[Rebuy Cart] Skipping initialization on checkout/order/editor pages');
      return;
    }
    
    isInitialized = true;

    // Set up initial state
    initializeCartState();

    // Setup comprehensive event listeners
    setupEventListeners();

    // Watch for Rebuy widgets and network requests
    setupNetworkInterception();

    // Start real-time cart monitoring
    startCartMonitoring();
  }

  function initializeCartState() {
    fetch(`${window.routes.cart_url}.js`)
      .then((response) => response.json())
      .then((cart) => {
        cartState = { ...cart };
      })
      .catch((error) => {
        // Silent error handling for production
      });
  }

  function setupEventListeners() {
    // Rebuy-specific events
    const rebuyEvents = [
      "rebuy:cart.change",
      "rebuy:cart.updated",
      "rebuy:product.added",
      "rebuy:item.added",

      "cart:updated",
    ];

    rebuyEvents.forEach((eventName) => {
      window.addEventListener(eventName, (event) => {
        handleRebuyEvent(event);
      });

      document.addEventListener(eventName, (event) => {
        handleRebuyEvent(event);
      });
    });

    // Watch for DOM changes
    observeRebuyWidgets();
  }

  function setupNetworkInterception() {
    // Intercept fetch requests
    const originalFetch = window.fetch;
    window.fetch = function (...args) {
      const [url, config] = args;

      if (url.includes("/cart/add") && config?.method === "POST") {
        return originalFetch.apply(this, args).then((response) => {
          if (response.ok) {
            // Schedule immediate cart check
            setTimeout(() => handlePotentialCartAdd(), 100);
          }
          return response;
        });
      }

      return originalFetch.apply(this, args);
    };

    // Intercept XMLHttpRequest
    const originalSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.send = function (data) {
      if (this._url && this._url.includes("/cart/add")) {
        this.addEventListener("loadend", () => {
          if (this.status >= 200 && this.status < 300) {
            setTimeout(() => handlePotentialCartAdd(), 100);
          }
        });
      }
      return originalSend.call(this, data);
    };

    const originalOpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (method, url) {
      this._url = url;
      return originalOpen.apply(this, arguments);
    };
  }

  function startCartMonitoring() {
    // Fast polling for immediate responsiveness
    cartCheckInterval = setInterval(() => {
      checkCartChanges();
    }, 500); // Check every 500ms
  }

  function handleRebuyEvent(event) {
    // Mark this as a potential addition to track
    const eventId = Date.now() + Math.random();
    pendingAdditions.add(eventId);

    // Check cart after a short delay
    setTimeout(() => {
      handlePotentialCartAdd(eventId);
    }, 200);
  }

  function handlePotentialCartAdd(eventId = null) {
    checkCartChanges(true, eventId);
  }

  function checkCartChanges(isTriggered = false, eventId = null) {
    fetch(`${window.routes.cart_url}.js`)
      .then((response) => response.json())
      .then((newCart) => {
        if (newCart.item_count > cartState.item_count) {
          // Find the newly added item(s)
          const newItems = findNewItems(cartState.items, newCart.items);

          if (newItems.length > 0) {
            const latestItem = newItems[0]; // Get the most recent addition

            // Clear pending addition if this was triggered
            if (eventId) {
              pendingAdditions.delete(eventId);
            }

            // Show cart notification immediately
            showCartNotification(latestItem, newCart);

            // Update cart UI
            updateCartUI(newCart);

            // Publish theme events
            publishCartUpdate(latestItem, newCart);
          }
        }

        // Update stored state
        cartState = { ...newCart };
      })
      .catch((error) => {
        // Silent error handling for production
      });
  }

  function findNewItems(oldItems, newItems) {
    const oldKeys = new Set(oldItems.map((item) => item.key));
    return newItems.filter((item) => !oldKeys.has(item.key));
  }

  function showCartNotification(item, cart) {
    const cartNotification = document.querySelector("cart-notification");
    const cartDrawer = document.querySelector("cart-drawer");

    if (cartNotification) {
      // Fetch and show theme cart notification
      fetchCartNotificationData(item.key)
        .then((cartData) => {
          cartNotification.renderContents(cartData);
        })
        .catch((error) => {
          showFallbackNotification(item);
        });
    } else if (cartDrawer) {
      // Show cart drawer
      fetchCartDrawerData()
        .then((cartData) => {
          cartDrawer.renderContents(cartData);
        })
        .catch((error) => {
          showFallbackNotification(item);
        });
    } else {
      // Fallback notification
      showFallbackNotification(item);
    }
  }

  function fetchCartNotificationData(itemKey) {
    const sections = [
      "cart-notification-product",
      "cart-notification-button",
      "cart-icon-bubble",
    ];
    const url = `${window.location.pathname}?sections=${sections.join(",")}`;

    return fetch(url)
      .then((response) => response.json())
      .then((data) => ({
        sections: data,
        key: itemKey,
      }));
  }

  function fetchCartDrawerData() {
    const sections = ["cart-drawer", "cart-icon-bubble"];
    const url = `${window.location.pathname}?sections=${sections.join(",")}`;

    return fetch(url)
      .then((response) => response.json())
      .then((data) => ({
        sections: data,
      }));
  }

  function updateCartUI(cart) {
    // Update cart count elements
    const countSelectors = [
      ".cart-count",
      "[data-cart-count]",
      ".cart-count-bubble",
      "#cart-icon-bubble .cart-count",
      ".header__icon--cart .cart-count",
    ];

    countSelectors.forEach((selector) => {
      document.querySelectorAll(selector).forEach((element) => {
        element.textContent = cart.item_count;
        element.classList.toggle("hidden", cart.item_count === 0);

        if (cart.item_count > 0) {
          element.style.display = "";
        }
      });
    });

    // Update cart icon bubble
    updateCartIconBubble();
  }

  function updateCartIconBubble() {
    const cartIconBubble = document.querySelector("#cart-icon-bubble");
    if (cartIconBubble) {
      fetch("/?section_id=cart-icon-bubble")
        .then((response) => response.text())
        .then((html) => {
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, "text/html");
          const newContent = doc.querySelector(".shopify-section");
          if (newContent) {
            cartIconBubble.innerHTML = newContent.innerHTML;
          }
        })
        .catch((error) => {
          // Silent error handling for production
        });
    }
  }

  function publishCartUpdate(item, cart) {
    if (
      typeof publish === "function" &&
      typeof PUB_SUB_EVENTS !== "undefined"
    ) {
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: "rebuy-integration",
        productVariantId: item.variant_id,
        cartData: { cart: cart, item: item },
      });
    }
  }

  function showFallbackNotification(item) {
    // Remove any existing notifications
    document
      .querySelectorAll(".rebuy-cart-notification")
      .forEach((n) => n.remove());

    const notification = document.createElement("div");
    notification.className = "rebuy-cart-notification";
    notification.setAttribute("role", "alert");
    notification.setAttribute("aria-live", "polite");
    notification.innerHTML = `
      <div class="rebuy-notification-content">
        <div class="rebuy-notification-icon">✅</div>
        <div class="rebuy-notification-text">
          <strong>${item.product_title}</strong><br>
          <small>Added to cart</small>






        </div>
        <button class="rebuy-notification-close" onclick="this.parentElement.parentElement.remove()">×</button>
      </div>
    `;

    // Styling
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #fff;
      border: 2px solid #4caf50;
      border-radius: 12px;
      padding: 16px;
      box-shadow: 0 6px 20px rgba(0,0,0,0.15);
      z-index: 10000;
      max-width: 320px;
      font-family: inherit;
      animation: slideInBounce 0.4s ease-out;
    `;

    // Add styles if not exists
    if (!document.querySelector("#rebuy-fallback-styles")) {
      const styles = document.createElement("style");
      styles.id = "rebuy-fallback-styles";
      styles.textContent = `
        @keyframes slideInBounce {
          0% { transform: translateX(100%) scale(0.8); opacity: 0; }
          60% { transform: translateX(-10px) scale(1.05); opacity: 1; }
          100% { transform: translateX(0) scale(1); opacity: 1; }
        }
        .rebuy-notification-content {
          display: flex;
          align-items: center;
          gap: 12px;
        }
        .rebuy-notification-icon {
          font-size: 24px;
          flex-shrink: 0;
        }
        .rebuy-notification-text {
          flex: 1;
          line-height: 1.3;
        }
        .rebuy-notification-close {
          background: none;
          border: none;
          font-size: 18px;
          cursor: pointer;
          padding: 4px;
          border-radius: 50%;
          width: 28px;
          height: 28px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #666;
          flex-shrink: 0;
        }
        .rebuy-notification-close:hover {
          background: rgba(0,0,0,0.1);
        }
      `;
      document.head.appendChild(styles);
    }

    document.body.appendChild(notification);

    // Auto remove after 4 seconds
    setTimeout(() => {
      if (notification.parentElement) {
        notification.style.animation = "slideInBounce 0.3s ease-out reverse";
        setTimeout(() => notification.remove(), 300);
      }
    }, 4000);
  }

  function observeRebuyWidgets() {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) {
            // Look for Rebuy widgets
            const rebuySelectors = [
              "[data-rebuy-id]",

              '[class*="rebuy"]',
              ".rebuy-widget",

              ".rebuy-button",
              '[id*="rebuy"]',
            ];

            const rebuyElements = node.querySelectorAll
              ? node.querySelectorAll(rebuySelectors.join(", "))
              : [];

            if (rebuyElements.length > 0) {
              setupWidgetListeners(rebuyElements);
            }

            // Check if the node itself is a Rebuy element
            if (node.matches && node.matches(rebuySelectors.join(", "))) {
              setupWidgetListeners([node]);
            }
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    // Setup listeners for existing widgets
    setupExistingWidgets();
  }

  function setupExistingWidgets() {
    const rebuySelectors = [
      "[data-rebuy-id]",

      '[class*="rebuy"]',
      ".rebuy-widget",

      '[id*="rebuy"]',
    ];

    const widgets = document.querySelectorAll(rebuySelectors.join(", "));
    if (widgets.length > 0) {
      setupWidgetListeners(widgets);
    }
  }

  function setupWidgetListeners(widgets) {
    widgets.forEach((widget) => {
      if (widget.dataset.rebuyIntegrated) return;

      // Listen for all clicks within the widget
      widget.addEventListener(
        "click",
        (event) => {
          const target = event.target.closest(
            'button, .button, [role="button"], a'
          );

          if (target) {
            const text = target.textContent.toLowerCase();
            const isAddButton =
              text.includes("add") ||
              text.includes("cart") ||
              text.includes("buy") ||
              target.classList.contains("rebuy-button") ||
              target.dataset.rebuyAction === "add-to-cart";

            if (isAddButton) {
              // Track this click
              const clickId = Date.now() + Math.random();
              pendingAdditions.add(clickId);

              // Add visual feedback
              target.style.opacity = "0.7";
              setTimeout(() => (target.style.opacity = ""), 1000);

              // Check for cart changes after delay
              setTimeout(() => handlePotentialCartAdd(clickId), 300);
            }
          }
        },
        true
      );

      widget.dataset.rebuyIntegrated = "true";
    });
  }

  // Loader management functions
  function showLoader(container) {
    const loader = container.querySelector(".rebuy-loader");
    if (loader) {
      loader.style.display = "flex";
    }
    container.removeAttribute("data-loaded");
    widgetLoadStates.set(container, false);
  }

  function hideLoader(container) {
    const loader = container.querySelector(".rebuy-loader");
    if (loader) {
      loader.style.display = "none";
    }
    container.setAttribute("data-loaded", "true");
    widgetLoadStates.set(container, true);

    // Show and animate widgets
    const widgets = container.querySelectorAll("[data-rebuy-id]");
    widgets.forEach((widget) => {
      widget.classList.add("rebuy-widget-loaded");

      // Ensure widget is visible
      if (widget.style.display === "none") {
        widget.style.display = "block";
      }

      // Force visibility
      widget.style.opacity = "1";
      widget.style.visibility = "visible";
    });
  }

  function checkWidgetContent(container) {
    // Check if Rebuy has populated content
    const widgets = container.querySelectorAll("[data-rebuy-id]");
    let hasContent = false;

    widgets.forEach((widget) => {
      // Check for various indicators that Rebuy has loaded content
      if (widget.children.length > 0) {
        // More comprehensive content checks
        const hasProducts =
          widget.querySelector(".rebuy-product") ||
          widget.querySelector("[data-product-id]") ||
          widget.querySelector(".rebuy-product-block") ||
          widget.querySelector(".rebuy-widget");

        const hasText = widget.innerText.trim().length > 0;
        const hasVisibleContent =
          widget.offsetHeight > 0 && widget.offsetWidth > 0;
        const notEmpty = widget.innerHTML.trim() !== "";

        if ((hasProducts || hasText) && hasVisibleContent && notEmpty) {
          hasContent = true;
        }
      }

      // Also check if the widget display style has changed from none
      const style = window.getComputedStyle(widget);
      if (style.display !== "none" && widget.children.length > 0) {
        hasContent = true;
      }
    });

    return hasContent;
  }

  function setupLoaderManagement() {
    const containers = document.querySelectorAll(".rebuy-upsell-container");

    containers.forEach((container) => {
      // Show loader initially
      showLoader(container);

      let hasHiddenLoader = false;
      let checkCount = 0;
      const maxChecks = 20; // Maximum number of checks

      // Function to hide loader once
      const hideLoaderOnce = () => {
        if (!hasHiddenLoader) {
          hasHiddenLoader = true;
          hideLoader(container);
          if (observer) observer.disconnect();
          if (checkInterval) clearInterval(checkInterval);
        }
      };

      // Set up mutation observer to detect when Rebuy populates content
      const observer = new MutationObserver((mutations) => {
        let shouldCheck = false;

        mutations.forEach((mutation) => {
          if (mutation.type === "childList" && mutation.addedNodes.length > 0) {
            shouldCheck = true;
          } else if (
            mutation.type === "attributes" &&
            (mutation.attributeName === "data-rebuy-id" ||
              mutation.attributeName === "class" ||
              mutation.attributeName === "style")
          ) {
            shouldCheck = true;
          }
        });

        if (shouldCheck) {
          // Check immediately
          if (checkWidgetContent(container)) {
            hideLoaderOnce();
          }
        }
      });

      // Observe the container and its subtree
      observer.observe(container, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["data-rebuy-id", "class", "style"],
      });

      // Periodic check with exponential backoff
      const checkInterval = setInterval(() => {
        checkCount++;

        if (checkWidgetContent(container)) {
          hideLoaderOnce();
        } else if (checkCount >= maxChecks) {
          // Force hide after max checks (even if no content)
          hideLoaderOnce();
        }
      }, 500); // Check every 500ms

      // Absolute fallback timeout to hide loader after 10 seconds
      setTimeout(() => {
        if (!hasHiddenLoader) {
          hideLoaderOnce();
        }
      }, 10000);
    });
  }

  // Initialize based on document state
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      init();
      // Set up loader management after a brief delay to ensure DOM is ready
      setTimeout(setupLoaderManagement, 250);
    });
  } else {
    setTimeout(() => {
      init();
      setupLoaderManagement();
    }, 100);
  }

  // Also watch for dynamic content loading (e.g., AJAX page transitions)
  window.addEventListener("shopify:section:load", () => {
    setTimeout(setupLoaderManagement, 250);
  });

  // Cleanup on unload
  window.addEventListener("beforeunload", () => {
    if (cartCheckInterval) {
      clearInterval(cartCheckInterval);
    }
  });
})();
